import React from 'react';
import { Outlet } from 'react-router';
import classNames from 'classnames';
import { observer } from 'mobx-react';

import { usePublicCompany } from 'client/hooks';
import RetinoLoader from 'components/RetinoLoader';
import { currentLanguage } from 'helpers/utils';
import { Button } from 'ui';

type Props = {
  hideFooter?: boolean;
  gray?: boolean;
};

function ClientLayout({ hideFooter = false, gray = true }: Props) {
  const { company } = usePublicCompany();

  if (company === null) {
    return <RetinoLoader />;
  }

  return (
    <div className={classNames('w-full', gray ? 'bg-gray-50' : 'bg-white')}>
      <div className="mx-auto min-h-screen max-w-screen-lg space-y-6 py-4 px-0 sm:p-4 md:p-6">
        <div className="flex flex-wrap justify-between gap-2">
          <div>
            {company.logo_url ? (
              <img src={currentLanguage(company.logo_url)} alt={currentLanguage(company.name)} className="h-20" />
            ) : (
              <span className="text-2xl font-medium text-gray-700">{currentLanguage(company.name)}</span>
            )}
          </div>
          {company.shop_url && (
            <div>
              <Button icon="external-link" size="tiny">
                <a href={currentLanguage(company.shop_url)}>
                  {interpolate(
                    gettext('Go to %(company_name)s'),
                    { company_name: currentLanguage(company.name) },
                    true,
                  )}
                </a>
              </Button>
            </div>
          )}
        </div>

        <div className="mt-8">
          <div>
            <Outlet />
          </div>

          {!hideFooter && (
            <footer className="footer footer-transparent mt-12 space-y-6 pb-12">
              <div className="company-logo">
                {company.logo_url ? (
                  <img
                    src={currentLanguage(company.logo_url)}
                    alt={currentLanguage(company.name)}
                    className="mx-auto w-32"
                  />
                ) : (
                  <div className="text-center font-medium text-gray-600">{currentLanguage(company.name)}</div>
                )}
              </div>
              <div className="mx-auto text-center text-sm text-gray-400">
                <a href="https://www.retino.com/" target="blank" rel="noopener noreferrer">
                  {gettext("We're using")}
                  <div className="mx-auto w-20 pt-2 text-gray-400">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1980 455">
                      <path
                        fill="currentColor"
                        d="M196 223c40 0 63-19 63-50v-1c0-33-24-50-64-50h-77v102h78M0 18h202c65 0 110 17 139 46 25 24 37 57 37 99v1c0 65-34 108-87 131l101 148H256l-86-129h-51v129H0V18zm617 235c-4-36-25-59-57-59-30 0-51 23-57 59h114m-224 28v-1c0-96 69-172 166-172 114 0 166 83 166 180 0 7 0 15-1 24H504c9 36 35 54 71 54 27 0 49-10 73-33l64 53c-31 40-77 67-143 67-103 0-177-69-177-171zm392 61V208h-39v-93h39V31h116v83h76v93h-76v111c0 23 10 32 31 32 15 0 29-4 43-10v90c-20 11-48 18-80 18-69 0-111-30-111-109zm237-227h116v330h-116V115M1021 0h120v85h-120V0zm471 242c2.06.01 11.79.04 26.21.09v-12c0-75-42-121-112-121-47 0-77 26-98 53v-46h-116v330h116V266c0-36 20-55 48-55 27 0 46 19 46 55v178h116V305l-55-63 29.03.04zm253-136c-92 0-182 64-194 152 38.51.15 91.31-.13 125 0 54-1-172-2-183-2 40 45 79 91 119 136 39-45 79-90 119-135h-54c7-41 39-50 68-50 41 0 71 34 71 75 0 41-27 75-69 75-24 0-45-12-57-30l-64 74c32 29 68 55 120 55 107 0 185-79 185-174 0-96-77-174-184-174z"
                      />
                    </svg>
                  </div>
                </a>
              </div>
            </footer>
          )}
        </div>
      </div>
    </div>
  );
}

export default observer(ClientLayout);
